import { Copy } from "lucide-react";
import { toast } from "sonner";

/**
 * One copy-to-clipboard button, identical everywhere it appears.
 *
 * Lifted from `features/customers/components/CustomerFacts.tsx`, which is where this shape was
 * settled. Seven files had grown their own `navigator.clipboard.writeText` by the time this was
 * extracted; new callers should use this one rather than adding an eighth.
 *
 * `stopPropagation` is load-bearing inside a clickable table row: without it, copying also fires
 * the row's onClick and opens a drawer the operator did not ask for.
 *
 * RICH COPY: pass `html` and the clipboard carries BOTH flavours — `text/html` for editors that
 * accept it (a ticket reply, a doc), `text/plain` for everything else. That is what lets a pasted
 * tracking number arrive in the ticket system as a working link rather than a bare string the
 * agent has to hand-link. Support is uneven, so the plain flavour must stand on its own.
 */
export function CopyButton({
  value,
  html,
  label,
  testId,
  stopPropagation,
  className,
}: {
  /** Plain-text flavour. Must be useful by itself — some targets only take this one. */
  value: string;
  /** Optional rich flavour, e.g. `<a href="…">…</a>`. Callers must escape it. */
  html?: string;
  label: string;
  testId?: string;
  stopPropagation?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={async (e) => {
        if (stopPropagation) e.stopPropagation();
        try {
          // `ClipboardItem` is the only way to put two flavours on the clipboard at once, and it
          // is not everywhere (older Firefox, non-secure contexts). Fall back to plain text rather
          // than failing the copy — losing the hyperlink beats losing the number.
          if (
            html &&
            typeof ClipboardItem !== "undefined" &&
            navigator.clipboard?.write
          ) {
            await navigator.clipboard.write([
              new ClipboardItem({
                "text/html": new Blob([html], { type: "text/html" }),
                "text/plain": new Blob([value], { type: "text/plain" }),
              }),
            ]);
          } else {
            await navigator.clipboard.writeText(value);
          }
          toast.success(`${label} copied`);
        } catch {
          // Clipboard access can be denied (permissions, insecure context). Say so rather than
          // silently doing nothing, which reads as "the button is broken".
          toast.error("Copy failed");
        }
      }}
      aria-label={`Copy ${label.toLowerCase()}`}
      title={`Copy ${label.toLowerCase()}`}
      data-testid={testId}
      className={
        "ml-1.5 inline-flex cursor-pointer items-center align-middle text-muted-foreground hover:text-foreground " +
        (className ?? "")
      }
    >
      <Copy className="h-3 w-3" />
    </button>
  );
}

/** Escape for interpolation into HTML text or a quoted attribute. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * The two clipboard flavours for a tracking number.
 *
 * Plain text keeps the URL alongside the number rather than dropping it: most ticket systems
 * auto-linkify a bare URL, so even the fallback arrives clickable. With no carrier URL both
 * flavours collapse to the number alone.
 */
export function trackingClipboard(
  number: string,
  url?: string | null,
): { value: string; html?: string } {
  if (!url) return { value: number };
  return {
    value: `${number} ${url}`,
    html: `<a href="${escapeHtml(url)}">${escapeHtml(number)}</a>`,
  };
}
